
import { jitsiLocalStorage } from '@jitsi/js-utils';

import { SERVER_URL_CHANGE_ENABLED, getFeatureFlag } from '../base/flags';
import { DEFAULT_LANGUAGE, LANGUAGES, i18next } from '../base/i18n';
import { createLocalTrack } from '../base/lib-meet-hour/functions';
import {
    getLocalParticipant,
    isLocalParticipantModerator
} from '../base/participants/functions';
import { toState } from '../base/redux';
import { parseStandardURIString } from '../base/util';
import { isFollowMeActive } from '../follow-me';
import { iAmVisitor } from '../visitors/functions';

import logger from './logger';


declare var interfaceConfig: Object;

/**
 * Used for web. Indicates if the setting section is enabled.
 *
 * @param {string} settingName - The name of the setting section as defined in
 * interface_config.js and SettingsMenu.js.
 * @returns {boolean} True to indicate that the given setting section
 * is enabled, false otherwise.
 */
export function isSettingEnabled(settingName: string) {
    return interfaceConfig.SETTINGS_SECTIONS.includes(settingName);
}

/**
 * Returns true if user is allowed to change Server URL.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @returns {boolean} True to indicate that user can change Server URL, false otherwise.
 */
export function isServerURLChangeEnabled(stateful: Object | Function) {
    const state = toState(stateful);
    const flag = getFeatureFlag(state, SERVER_URL_CHANGE_ENABLED, true);

    return flag;
}

/**
 * Normalizes a URL entered by the user.
 * FIXME: Consider adding this to base/util/uri.
 *
 * @param {string} url - The URL to validate.
 * @returns {string|null} - The normalized URL, or null if the URL is invalid.
 */
export function normalizeUserInputURL(url: string) {


    if (url) {
        let processedUrl = url.replace(/\s/g, '').toLowerCase();

        const urlRegExp = new RegExp('^(\\w+://)?(.+)$');
        const urlComponents = urlRegExp.exec(processedUrl);

        if (urlComponents && (!urlComponents[1]
                || !urlComponents[1].startsWith('http'))) {
            processedUrl = `https://${urlComponents[2]}`;
        }

        const parsedURI = parseStandardURIString(processedUrl);

        if (!parsedURI.host) {
            return null;
        }

        return parsedURI.toString();
    }

    return url;
}

/**
 * Used for web. Returns whether or not only Device Selection is configured to
 * display as a setting.
 *
 * @returns {boolean}
 */
export function shouldShowOnlyDeviceSelection() {
    return interfaceConfig.SETTINGS_SECTIONS.length === 1
        && isSettingEnabled('devices');
}

/**
 * Returns the properties for the "More" tab from settings dialog from Redux
 * state.
 *
 * @param {(Function|Object)} stateful -The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @returns {Object} - The properties for the "More" tab from settings dialog.
 */
export function getMoreTabProps(stateful: Object | Function) {
    const state = toState(stateful);
    const language = i18next.language || DEFAULT_LANGUAGE;
    const {
        conference,
        followMeEnabled,
        startAudioMutedPolicy,
        startVideoMutedPolicy,
        deleteCache
    } = state['features/base/conference'];
    const storedDisableAutoPiP = jitsiLocalStorage.getItem('features/base/settings/disableAutoPIP');
    const disableAutoPiP
        = storedDisableAutoPiP === true || storedDisableAutoPiP === 'true';
    const followMeActive = isFollowMeActive(state);
    const configuredTabs = interfaceConfig.SETTINGS_SECTIONS || [];

    // The settings sections to display.
    const showModeratorSettings = Boolean(
        conference
            && configuredTabs.includes('moderator')
            && isLocalParticipantModerator(state));

    return {
        currentLanguage: language,
        followMeActive: Boolean(conference && followMeActive),
        followMeEnabled: Boolean(conference && followMeEnabled),
        languages: LANGUAGES,
        iAmVisitor: iAmVisitor(state),
        showLanguageSettings: configuredTabs.includes('language'),
        showModeratorSettings,
        showPrejoinSettings: state['features/base/config'].prejoinPageEnabled,
        showPrejoinPage: !state['features/base/settings'].userSelectedSkipPrejoin,
        startAudioMuted: Boolean(conference && startAudioMutedPolicy),
        startVideoMuted: Boolean(conference && startVideoMutedPolicy),
        deleteCache: Boolean(conference && deleteCache),
        disableAutoPip: disableAutoPiP,
        areChatNotificationsHidden: Boolean(state['features/chat']?.areChatNotificationsHidden
            || jitsiLocalStorage.getItem('hide_chat_notifications') === 'true'),
        showScreenWhileScreenSharing: jitsiLocalStorage.getItem('show_screen_while_screensharing') === 'true'
    };
}

/**
 * Returns the properties for the "Profile" tab from settings dialog from Redux
 * state.
 *
 * @param {(Function|Object)} stateful -The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @returns {Object} - The properties for the "Profile" tab from settings
 * dialog.
 */
export function getProfileTabProps(stateful: Object | Function) {
    const state = toState(stateful);
    const {
        authEnabled,
        authLogin,
        conference
    } = state['features/base/conference'];
    const localParticipant = getLocalParticipant(state);
    const { userDetails } = state['features/base/mtApi'];

    return {
        authEnabled: Boolean(conference && authEnabled),
        authLogin,
        displayName: localParticipant?.name,
        email: localParticipant?.email,
        designation: localParticipant?.designation || userDetails?.designation,
        organization: localParticipant?.organization || userDetails?.organization
    };
}

/**
 * Returns a promise which resolves with a list of objects containing
 * all the video mhTracks and appropriate errors for the given device ids.
 *
 * @param {string[]} ids - The list of the camera ids for wich to create tracks.
 * @param {number} [timeout] - A timeout for the createLocalTrack function call.
 *
 * @returns {Promise<Object[]>}
 */
export function createLocalVideoTracks(ids: string[], timeout: ?number) {
    return Promise.all(ids.map(deviceId => createLocalTrack('video', deviceId, timeout)
                   .then(mhTrack => {
                       return {
                           mhTrack,
                           deviceId
                       };
                   })
                   .catch(error => {
                       // The reason used to be discarded entirely, which left no way to tell a camera the
                       // machine cannot open right now (NotReadableError - another application, a USB
                       // bandwidth limit, or a handle this page leaked) from one that is genuinely gone.
                       // The entry is still returned so the device stays selectable; only its thumbnail is
                       // missing. See VideoSettingsContent._renderPreviewEntry.
                       logger.warn(`Could not build a preview for camera ${deviceId}: `
                            + `${error?.name || 'unknown'} ${error?.message || ''}`);

                       return {
                           mhTrack: null,
                           deviceId,
                           error: 'deviceSelection.previewUnavailable'
                       };
                   })));
}


/**
 * Returns a promise which resolves with a list of objects containing
 * the audio track and the corresponding audio device information.
 *
 * @param {Object[]} devices - A list of microphone devices.
 * @param {number} [timeout] - A timeout for the createLocalTrack function call.
 * @returns {Promise<{
 *   deviceId: string,
 *   hasError: boolean,
 *   mhTrack: Object,
 *   label: string
 * }[]>}
 */
export function createLocalAudioTracks(devices: Object[], timeout: ?number) {
    return Promise.all(
        devices.map(async ({ deviceId, label }) => {
            let mhTrack = null;
            let hasError = false;

            try {
                mhTrack = await createLocalTrack('audio', deviceId, timeout);
            } catch {
                hasError = true;
            }

            return {
                deviceId,
                hasError,
                mhTrack,
                label
            };
        }));
}

/**
 * Returns the visibility state of the audio settings.
 *
 * @param {Object} state - The state of the application.
 * @returns {boolean}
 */
export function getAudioSettingsVisibility(state: Object) {
    return state['features/settings'].audioSettingsVisible;
}

/**
 * Returns the visibility state of the video settings.
 *
 * @param {Object} state - The state of the application.
 * @returns {boolean}
 */
export function getVideoSettingsVisibility(state: Object) {
    return state['features/settings'].videoSettingsVisible;
}
